import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { jamRegistrationsApi } from "./client";

const keys = {
  events: ["jam-events"] as const,
  event: (id: string) => ["jam-events", id] as const,
  registrations: (query: Record<string, unknown>) =>
    ["jam-registrations", query] as const,
  registration: (id: string) => ["jam-registrations", id] as const,
};

export function useJamEventsQuery() {
  return useQuery({
    queryKey: keys.events,
    queryFn: () => jamRegistrationsApi.listEvents(),
  });
}

export function useJamEventQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.event(id ?? ""),
    queryFn: () => jamRegistrationsApi.getEvent(id!),
    enabled: !!id,
  });
}

export function useInitializeFanglaoJam2026Mutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jamRegistrationsApi.initializeFanglaoJam2026(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useUpdateJamEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof jamRegistrationsApi.updateEvent>[1];
    }) => jamRegistrationsApi.updateEvent(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.event(id) });
      qc.invalidateQueries({ queryKey: keys.events });
    },
  });
}

export function useUpdateJamCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      categoryId,
      body,
    }: {
      eventId: string;
      categoryId: string;
      body: Parameters<typeof jamRegistrationsApi.updateCategory>[2];
    }) => jamRegistrationsApi.updateCategory(eventId, categoryId, body),
    onSuccess: (_, { eventId }) => {
      qc.invalidateQueries({ queryKey: keys.event(eventId) });
    },
  });
}

export function useJamRegistrationsQuery(
  query: OffsetPageQueryDTO & {
    eventId?: string;
    status?: string;
    categoryId?: string;
    search?: string;
  },
) {
  return useQuery({
    queryKey: keys.registrations(query),
    queryFn: () => jamRegistrationsApi.listRegistrations(query),
    enabled: !!query.eventId,
  });
}

export function useJamRegistrationQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.registration(id ?? ""),
    queryFn: () => jamRegistrationsApi.getRegistration(id!),
    enabled: !!id,
  });
}

export function useUpdateJamRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof jamRegistrationsApi.updateRegistration>[1];
    }) => jamRegistrationsApi.updateRegistration(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.registration(id) });
      qc.invalidateQueries({ queryKey: ["jam-registrations"] });
      qc.invalidateQueries({ queryKey: keys.events });
      qc.invalidateQueries({ queryKey: ["jam-events"] });
    },
  });
}

export function useConvertJamRegistrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof jamRegistrationsApi.convertRegistration>[1];
    }) => jamRegistrationsApi.convertRegistration(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.registration(id) });
      qc.invalidateQueries({ queryKey: ["jam-registrations"] });
    },
  });
}
